/** A bounded retry for read-only statistics, including a fresh timeout per attempt. */
export async function readCampaignStats<T>(
  read: (signal: AbortSignal) => Promise<T>,
  signal: AbortSignal,
): Promise<T> {
  for (let attempt = 0; ; attempt++) {
    signal.throwIfAborted();
    const controller = new AbortController();
    let timedOut = false;
    const cancel = () => controller.abort(signal.reason);
    signal.addEventListener('abort', cancel, { once: true });
    const timeout = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, 30_000);
    let failure: unknown;
    try {
      return await read(controller.signal);
    } catch (error) {
      failure = error;
    } finally {
      clearTimeout(timeout);
      signal.removeEventListener('abort', cancel);
    }
    signal.throwIfAborted();
    const error = failure as { code?: string; response?: { status?: number } } | null;
    const transient = timedOut || ['ERR_NETWORK', 'ECONNABORTED', 'ETIMEDOUT'].includes(error?.code ?? '') ||
      [502, 503, 504].includes(error?.response?.status ?? 0);
    if (attempt > 0 || !transient) throw failure;
    await new Promise<void>((resolve, reject) => {
      const cancelDelay = () => {
        clearTimeout(delay);
        reject(signal.reason);
      };
      const delay = setTimeout(() => {
        signal.removeEventListener('abort', cancelDelay);
        resolve();
      }, 1000);
      signal.addEventListener('abort', cancelDelay, { once: true });
      if (signal.aborted) cancelDelay();
    });
  }
}
